import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getPlanReport } from "../../actions/planReportAction";
import "./PlanAnalysis.css";

// ── Constants ──────────────────────────────────────────────────────────────

const CATEGORY_META = {
  green:  { label: "Going Well",      color: "#22c55e", desc: "Active with meaningful progress" },
  orange: { label: "Can Be Better",   color: "#f59e0b", desc: "Active but progress has slowed" },
  red:    { label: "No Progress",     color: "#ef4444", desc: "No meaningful updates" },
};

const CATEGORY_ORDER = ["green", "orange", "red"];

// ── Sub-components ─────────────────────────────────────────────────────────

const CategoryBar = ({ categoryCounts, total }) => (
  <div className="pa-catbar-wrap">
    <div className="pa-catbar-track">
      {CATEGORY_ORDER.map((key) => {
        const count = categoryCounts[key] || 0;
        if (count === 0) return null;
        const pct = (count / total) * 100;
        return (
          <div
            key={key}
            className="pa-catbar-seg"
            style={{ width: `${pct}%`, background: CATEGORY_META[key].color }}
            title={`${CATEGORY_META[key].label}: ${count}`}
          />
        );
      })}
    </div>
    <div className="pa-catbar-legend">
      {CATEGORY_ORDER.map((key) => {
        const count = categoryCounts[key] || 0;
        if (count === 0) return null;
        return (
          <div key={key} className="pa-catbar-legend-item">
            <span className="pa-catbar-dot" style={{ background: CATEGORY_META[key].color }} />
            <span>{CATEGORY_META[key].label}</span>
            <span className="pa-catbar-count">{count}</span>
          </div>
        );
      })}
    </div>
  </div>
);

const NodeCard = ({ node }) => {
  const meta = CATEGORY_META[node.category];
  const lastSeen = node.daysSinceUpdate !== null ? `${node.daysSinceUpdate}d ago` : "Never updated";

  return (
    <div className="pa-node-card">
      {/* Meta row */}
      <div className="pa-node-meta">
        {node.updateCount > 0 && (
          <span className="pa-node-updates">
            {node.updateCount} {node.updateCount === 1 ? "update" : "updates"}
          </span>
        )}
        <span className="pa-node-date">{lastSeen}</span>
      </div>

      {/* Text */}
      <Link to={`/dashboard/note-details/${node.nodeId}`} className="pa-node-text">
        {node.note.length > 200 ? node.note.slice(0, 200) + "…" : node.note}
      </Link>

      {/* Actions — fade in on hover like Note.jsx */}
      {(node.category === "red" || node.category === "orange") && (
        <div className="pa-node-actions">
          <Link to={`/dashboard/note-details/${node.nodeId}`} className="pa-node-cta">
            Update Now
          </Link>
        </div>
      )}
    </div>
  );
};

const CategorySection = ({ categoryKey, nodes }) => {
  const [open, setOpen] = useState(true);
  const meta = CATEGORY_META[categoryKey];
  if (!nodes || nodes.length === 0) return null;

  return (
    <div className="pa-cat-section">
      <button className="pa-cat-header" onClick={() => setOpen((v) => !v)}>
        <span className="pa-cat-indicator" style={{ background: meta.color }} />
        <span className="pa-cat-name">{meta.label}</span>
        <span className="pa-cat-count">{nodes.length}</span>
        <span className="pa-cat-desc">{meta.desc}</span>
        <i className={`bx bx-chevron-down pa-cat-chevron ${open ? "open" : ""}`} />
      </button>
      {open && (
        <div className="pa-cat-nodes">
          {nodes.map((n, i) => <NodeCard key={i} node={n} />)}
        </div>
      )}
    </div>
  );
};

const SkeletonLoader = () => (
  <div className="pa-skeleton">
    <div className="pa-sk-bar pa-sk-bar--wide" />
    <div className="pa-sk-bar" />
    <div className="pa-sk-bar pa-sk-bar--short" />
    <div className="pa-sk-cards">
      {[1, 2, 3].map((i) => <div key={i} className="pa-sk-card" />)}
    </div>
  </div>
);

// ── Main component ─────────────────────────────────────────────────────────

const PlanAnalysis = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("loading");
  const [report, setReport] = useState(null);
  const [cooldownDaysLeft, setCooldownDaysLeft] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await getPlanReport(id);
        if (!cancelled) {
          setReport(data.report);
          setCooldownDaysLeft(data.cooldownDaysLeft || 0);
          setStatus("done");
        }
      } catch (err) {
        if (!cancelled) {
          setErrorMsg(err.response?.data?.message || "Failed to generate report. Please try again.");
          setStatus("error");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  const generate = async () => {
    setStatus("loading");
    try {
      const data = await getPlanReport(id);
      setReport(data.report);
      setCooldownDaysLeft(data.cooldownDaysLeft || 0);
      setStatus("done");
    } catch (err) {
      setErrorMsg(err.response?.data?.message || "Failed to generate report. Please try again.");
      setStatus("error");
    }
  };

  const grouped = {};
  if (report) {
    for (const key of CATEGORY_ORDER) grouped[key] = [];
    for (const n of report.nodeReports || []) {
      if (grouped[n.category]) grouped[n.category].push(n);
    }
  }

  const total = report
    ? Object.values(report.categoryCounts).reduce((a, b) => a + b, 0)
    : 0;

  return (
    <div className="pa-page five-year-dashboard dark">
    <div className="pa-inner">
      {/* Header */}
      <div className="pa-header">
        <button className="pa-back" onClick={() => navigate(-1)}>← Vision</button>
        <h2 className="pa-title">Plan Health Report</h2>
      </div>

      {/* Loading */}
      {status === "loading" && (
        <div className="pa-loading-wrap">
          <SkeletonLoader />
          <p className="pa-loading-text">Analysing your plan...</p>
        </div>
      )}

      {/* Error */}
      {status === "error" && (
        <div className="pa-error">
          <p>{errorMsg}</p>
          <button className="pa-generate-btn" onClick={generate}>Retry</button>
        </div>
      )}

      {/* Report */}
      {status === "done" && report && (
        <div className="pa-report">
          {cooldownDaysLeft > 0 && (
            <p className="pa-cache-note">
              Next report available in {cooldownDaysLeft} {cooldownDaysLeft === 1 ? "day" : "days"}.
            </p>
          )}

          {/* Node breakdown bar */}
          <div className="pa-section">
            <h3 className="pa-section-title">Record Breakdown</h3>
            <CategoryBar categoryCounts={report.categoryCounts} total={total} />
          </div>

          {/* AI Summary */}
          {report.aiSummary && (
            <div className="pa-section">
              <h3 className="pa-section-title">Summary</h3>
              <div className="pa-summary-card">
                <p className="pa-summary-text">{report.aiSummary}</p>
              </div>
            </div>
          )}

          {/* Nodes by category */}
          <div className="pa-section">
            <h3 className="pa-section-title">Records</h3>
            {CATEGORY_ORDER.map((key) => (
              <CategorySection key={key} categoryKey={key} nodes={grouped[key]} />
            ))}
          </div>

          {/* Regenerate */}
          <button
            className="pa-regen-btn"
            onClick={generate}
            disabled={cooldownDaysLeft > 0}
            title={cooldownDaysLeft > 0 ? `Available in ${cooldownDaysLeft} ${cooldownDaysLeft === 1 ? "day" : "days"}` : undefined}
          >
            {cooldownDaysLeft > 0 ? `Available in ${cooldownDaysLeft} ${cooldownDaysLeft === 1 ? "day" : "days"}` : "Regenerate Report"}
          </button>
        </div>
      )}
    </div>
    </div>
  );
};

export default PlanAnalysis;
